/**
 * Offline sync queue for Setubuild.OS.
 *
 * When a mutation (e.g. attendance punch) is attempted without network,
 * we persist the payload to localStorage and replay it when the browser
 * reports we're back online.
 *
 * We keep the queue tiny — one bucket per feature (currently: attendance).
 * Uses localStorage instead of IndexedDB for simplicity + universal support
 * across construction-site Android devices with old WebViews.
 */

const KEY = "setuos_offline_queue_v1";

const load = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : { attendance: [] };
  } catch {
    return { attendance: [] };
  }
};

const save = (q) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(q));
  } catch (e) {
    // Storage full — drop oldest attendance items to make space.
    const q2 = { ...q, attendance: q.attendance.slice(-40) };
    try { localStorage.setItem(KEY, JSON.stringify(q2)); } catch (_) {}
  }
};

export const isOnline = () =>
  typeof navigator === "undefined" ? true : navigator.onLine;

export const queueSize = () => {
  const q = load();
  return (q.attendance || []).length;
};

export const enqueueAttendance = (payload) => {
  const q = load();
  q.attendance = q.attendance || [];
  q.attendance.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    payload,
  });
  save(q);
  return q.attendance.length;
};

/**
 * Drain the attendance queue.
 * @param {(payload: object) => Promise<any>} sender - function that performs the network call
 * @returns {Promise<{ok: number, fail: number}>}
 */
export const drainAttendance = async (sender) => {
  const q = load();
  const items = q.attendance || [];
  if (!items.length) return { ok: 0, fail: 0 };

  let ok = 0;
  const remaining = [];
  for (const item of items) {
    try {
      await sender(item.payload);
      ok += 1;
    } catch (e) {
      // Keep for next attempt (e.g., server down but network alive)
      remaining.push(item);
    }
  }
  save({ ...q, attendance: remaining });
  return { ok, fail: remaining.length };
};

export const clearAttendance = () => {
  const q = load();
  save({ ...q, attendance: [] });
};

/**
 * Subscribe to online/offline transitions and queue-size changes.
 * Returns an unsubscribe function.
 */
export const subscribeOnline = (cb) => {
  const online = () => cb({ online: true, size: queueSize() });
  const offline = () => cb({ online: false, size: queueSize() });
  window.addEventListener("online", online);
  window.addEventListener("offline", offline);
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) cb({ online: isOnline(), size: queueSize() });
  });
  return () => {
    window.removeEventListener("online", online);
    window.removeEventListener("offline", offline);
  };
};
